import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    Dialog,
    DialogContent,
    Chip,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    PushPin as PinIcon,
    PushPinOutlined as PinOutlinedIcon,
    Comment as CommentIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../hooks/useFetch';
import useDeleteItem from '../../../../hooks/useDeleteItem';
import usePostData from '../../../../hooks/usePostData';
import useFetchAll from '../../../../hooks/useFetchAll';
import RTL from '../../../../utils/RTL';
import { toPersianDigits } from '../../../../utils/convertNumbers';
import { formatJalaliDate, formatJalaliDateTime } from '../../../../utils/formatJalaliDate';
import { API } from '../../../../utils/apiRoutes';
import StandardTable from '../../../common/table/StandardTable';
import ConfirmationDialog from '../ConfirmationDialog';
import FloatingSnackbar from '../../../common/FloatingSnackbar';
import SearchableAutocomplete from '../../../SearchableAutocomplete';
import api from '../../../../config/axiosConfig';
import { useAuth } from '../../../../context/AuthContext';
import ReplyThread from './ReplyThread';

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'پایین', color: 'default' },
    { value: 'normal', label: 'عادی', color: 'info' },
    { value: 'high', label: 'بالا', color: 'warning' },
    { value: 'urgent', label: 'فوری', color: 'error' },
];

function ColleagueNotes() {
    const theme = useTheme();
    const { user: currentUser } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        priority: 'normal',
        is_pinned: false,
        target_users: [],
        target_roles: [],
        tags: [],
    });
    const [tagInput, setTagInput] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [myNotesFilter, setMyNotesFilter] = useState(false);
    const [pinnedFilter, setPinnedFilter] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const [replyDialogOpen, setReplyDialogOpen] = useState(false);
    const [selectedNoteForReply, setSelectedNoteForReply] = useState(null);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();
    const [postData, isCreating] = usePostData(API.colleagueNotes);
    const [isUpdating, setIsUpdating] = useState(false);

    // Fetch staff users only when dialog is open (lazy loading)
    const { data: staffUsers } = useFetchAll('/accounts/api/v1/profile/staffs/', {
        skip: !formDialogOpen
    });

    // Debounce search
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Build API params
    const apiParams = useMemo(() => {
        const params = {};
        if (debouncedSearch) {
            params.search = debouncedSearch;
        }
        if (priorityFilter !== 'all') {
            params.priority = priorityFilter;
        }
        if (myNotesFilter) {
            params.my_notes = 'true';
        }
        if (pinnedFilter) {
            params.is_pinned = 'true';
        }
        return params;
    }, [debouncedSearch, priorityFilter, myNotesFilter, pinnedFilter]);

    const { data, loading, error, refetch } = useFetch(API.colleagueNotes, {
        params: apiParams,
        skip: false,
    });

    const notes = data?.results || data || [];
    const totalCount = data?.count || notes.length;
    
    // Log error for debugging
    React.useEffect(() => {
        if (error) {
            console.error('ColleagueNotes API error:', error);
        }
    }, [error]);

    const handleCreate = useCallback(() => {
        setSelectedNote(null);
        setFormData({
            title: '',
            content: '',
            priority: 'normal',
            is_pinned: false,
            target_users: [],
            target_roles: [],
            tags: [],
        });
        setTagInput('');
        setFormDialogOpen(true);
    }, []);

    const handleEdit = useCallback((note) => {
        setSelectedNote(note);
        setFormData({
            title: note.title || '',
            content: note.content || '',
            priority: note.priority || 'normal',
            is_pinned: note.is_pinned || false,
            target_users: note.target_users_display || [],
            target_roles: note.target_roles_display || [],
            tags: note.tags || [],
        });
        setTagInput('');
        setFormDialogOpen(true);
    }, []);

    const handleDelete = useCallback((note) => {
        setNoteToDelete(note);
        setDeleteDialogOpen(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!noteToDelete) return;
        try {
            await deleteItem(`${API.colleagueNotes}${noteToDelete.id}/`);
            setSnackbar({
                open: true,
                message: 'یادداشت با موفقیت حذف شد.',
                severity: 'success',
            });
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در حذف یادداشت',
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setNoteToDelete(null);
        }
    }, [noteToDelete, deleteItem, refetch]);

    const handleFormChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleAddTag = useCallback(() => {
        if (tagInput.trim()) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), tagInput.trim()],
            }));
            setTagInput('');
        }
    }, [tagInput]);

    const handleRemoveTag = useCallback((index) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index),
        }));
    }, []);

    const handleTogglePin = useCallback(async (note) => {
        try {
            await api.patch(`${API.colleagueNotes}${note.id}/`, {
                is_pinned: !note.is_pinned,
            });
            refetch();
            setSnackbar({
                open: true,
                message: note.is_pinned ? 'یادداشت از حالت ثابت خارج شد.' : 'یادداشت ثابت شد.',
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || 'خطا در به‌روزرسانی',
                severity: 'error',
            });
        }
    }, [refetch]);

    const handleFormSubmit = useCallback(async () => {
        try {
            const payload = {
                title: formData.title || null,
                content: formData.content,
                priority: formData.priority,
                is_pinned: formData.is_pinned,
                tags: formData.tags || [],
                target_users: formData.target_users.map(u => u.id || u),
                target_roles: formData.target_roles.map(r => r.id || r),
            };

            if (selectedNote) {
                // Update existing note
                setIsUpdating(true);
                await api.patch(`${API.colleagueNotes}${selectedNote.id}/`, payload);
                setSnackbar({
                    open: true,
                    message: 'یادداشت با موفقیت به‌روزرسانی شد.',
                    severity: 'success',
                });
            } else {
                // Create new note
                const result = await postData(payload);
                if (result && result.success) {
                    setSnackbar({
                        open: true,
                        message: 'یادداشت با موفقیت ایجاد شد.',
                        severity: 'success',
                    });
                } else {
                    const errorMsg = result?.error || 'خطا در ایجاد یادداشت';
                    throw new Error(errorMsg);
                }
            }
            setFormDialogOpen(false);
            setSelectedNote(null);
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({
                open: true,
                message: err?.response?.data?.detail || err?.message || 'خطا در ذخیره یادداشت',
                severity: 'error',
            });
        } finally {
            setIsUpdating(false);
        }
    }, [formData, selectedNote, postData, refetch]);

    const handleCloseForm = useCallback(() => {
        setFormDialogOpen(false);
        setSelectedNote(null);
        setFormData({
            title: '',
            content: '',
            priority: 'normal',
            is_pinned: false,
            target_users: [],
            target_roles: [],
            tags: [],
        });
        setTagInput('');
    }, []);

    // Prepare staff user options
    const staffUserOptions = useMemo(() => {
        return (staffUsers || []).map(staff => ({
            id: staff.user?.id,
            name: `${staff.user?.first_name || ''} ${staff.user?.last_name || ''}`.trim() || staff.user?.email,
            email: staff.user?.email,
        }));
    }, [staffUsers]);

    // Table configuration
    const columns = useMemo(() => [
        {
            id: 'is_pinned',
            label: '',
            align: 'center',
            width: 50,
            render: (row) => (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePin(row);
                    }}
                    color={row.is_pinned ? 'primary' : 'default'}
                >
                    {row.is_pinned ? <PinIcon fontSize="small" /> : <PinOutlinedIcon fontSize="small" />}
                </IconButton>
            ),
        },
        {
            id: 'priority',
            label: 'اولویت',
            compact: true,
            align: 'center',
            render: (row) => {
                const priorityOption = PRIORITY_OPTIONS.find(p => p.value === row.priority);
                return (
                    <Chip
                        label={priorityOption?.label || row.priority}
                        color={priorityOption?.color || 'default'}
                        size="small"
                    />
                );
            },
        },
        {
            id: 'title',
            label: 'عنوان',
            align: 'start',
            render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: row.title ? 'medium' : 'normal' }}>
                    {row.title || 'بدون عنوان'}
                </Typography>
            ),
        },
        {
            id: 'content',
            label: 'محتوا',
            align: 'start',
            render: (row) => (
                <Typography
                    variant="body2"
                    sx={{
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {row.content}
                </Typography>
            ),
        },
        {
            id: 'author',
            label: 'نویسنده',
            align: 'start',
            priority: 'secondary',
            render: (row) => (
                <Typography variant="body2">
                    {row.author_name || row.author_email || '—'}
                </Typography>
            ),
        },
        {
            id: 'targeting',
            label: 'برای',
            align: 'start',
            priority: 'secondary',
            render: (row) => {
                const hasUsers = row.target_users_display && row.target_users_display.length > 0;
                const hasRoles = row.target_roles_display && row.target_roles_display.length > 0;
                
                if (!hasUsers && !hasRoles) {
                    return <Typography variant="body2" color="text.secondary">همه</Typography>;
                }
                
                return (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {hasUsers && row.target_users_display.slice(0, 2).map((u, idx) => (
                            <Chip key={`user-${idx}`} label={u.name} size="small" variant="outlined" />
                        ))}
                        {hasRoles && row.target_roles_display.map((r, idx) => (
                            <Chip key={`role-${idx}`} label={r.name} size="small" color="primary" />
                        ))}
                        {(hasUsers && row.target_users_display.length > 2) && (
                            <Chip label={`+${row.target_users_display.length - 2}`} size="small" />
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'created_at',
            label: 'تاریخ و\nساعت',
            align: 'center',
            priority: 'secondary',
            render: (row) => {
                if (!row.created_at) {
                    return <Typography variant="body2" color="text.secondary">—</Typography>;
                }
                const formattedDateTime = formatJalaliDateTime(row.created_at);
                return (
                    <Typography variant="body2">
                        {formattedDateTime ? toPersianDigits(formattedDateTime) : '—'}
                    </Typography>
                );
            },
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            align: 'center',
            render: (row) => {
                // Check if user can edit/delete: author, superuser, or manager
                // Secretary can only edit/delete their own notes (isAuthor check)
                const isAuthor = row.author === currentUser?.id;
                const isSuperuser = currentUser?.is_superuser;
                const userRole = currentUser?.staff_role?.name;
                const canModify = isAuthor || isSuperuser || userRole === 'manager';
                
                return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedNoteForReply(row);
                                setReplyDialogOpen(true);
                            }}
                            color="info"
                            aria-label="پاسخ‌ها"
                            title="مشاهده و پاسخ"
                        >
                            <CommentIcon fontSize="small" />
                        </IconButton>
                        {canModify && (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(row);
                                    }}
                                    color="primary"
                                    aria-label="ویرایش"
                                    title="ویرایش"
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(row);
                                    }}
                                    aria-label="حذف"
                                    title="حذف"
                                    color="error"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>
                );
            },
        },
    ], [handleEdit, handleDelete, handleTogglePin, currentUser]);

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                {formDialogOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button startIcon={<BackIcon />} variant="outlined" onClick={handleCloseForm}>
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedNote ? 'ویرایش یادداشت' : 'ایجاد یادداشت جدید'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="عنوان (اختیاری)"
                                    value={formData.title}
                                    onChange={(e) => handleFormChange('title', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                                <TextField
                                    label="محتوا *"
                                    value={formData.content}
                                    onChange={(e) => handleFormChange('content', e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={8}
                                    variant="outlined"
                                    required
                                />
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>اولویت</InputLabel>
                                        <Select
                                            value={formData.priority}
                                            label="اولویت"
                                            onChange={(e) => handleFormChange('priority', e.target.value)}
                                        >
                                            {PRIORITY_OPTIONS.map(opt => (
                                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.is_pinned}
                                                onChange={(e) => handleFormChange('is_pinned', e.target.checked)}
                                            />
                                        }
                                        label="ثابت کردن"
                                    />
                                </Box>

                                {/* Target Users - Simplified for MVP */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        کاربران هدف (اختیاری - اگر خالی باشد برای همه نمایش داده می‌شود)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        {formData.target_users.map((user, index) => (
                                            <Chip
                                                key={index}
                                                label={user.name || user.email}
                                                onDelete={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        target_users: prev.target_users.filter((_, i) => i !== index),
                                                    }));
                                                }}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                    <SearchableAutocomplete
                                        apiUrl="/accounts/api/v1/profile/staffs/"
                                        placeholder="جستجوی کاربر..."
                                        getOptionLabel={(option) => {
                                            const user = option?.user;
                                            if (!user) return '';
                                            const first = user.first_name?.trim() || '';
                                            const last = user.last_name?.trim() || '';
                                            const full = `${first} ${last}`.trim();
                                            return full || user.email || '';
                                        }}
                                        getOptionValue={(option) => option?.user?.id}
                                        onChange={(value, fullOption) => {
                                            if (value && fullOption) {
                                                const user = fullOption.user;
                                                const newUser = {
                                                    id: user.id,
                                                    name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
                                                    email: user.email,
                                                };
                                                if (!formData.target_users.find(u => u.id === newUser.id)) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        target_users: [...prev.target_users, newUser],
                                                    }));
                                                }
                                            }
                                        }}
                                        size="small"
                                    />
                                </Box>

                                {/* Tags */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        برچسب‌ها
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                        {formData.tags && formData.tags.map((tag, index) => (
                                            <Chip
                                                key={index}
                                                label={tag}
                                                onDelete={() => handleRemoveTag(index)}
                                                size="small"
                                            />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="افزودن برچسب..."
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                            sx={{ flexGrow: 1 }}
                                        />
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleAddTag}
                                        >
                                            افزودن
                                        </Button>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                                    <Button onClick={handleCloseForm}>انصراف</Button>
                                    <Button
                                        onClick={handleFormSubmit}
                                        variant="contained"
                                        disabled={!formData.content.trim() || isCreating || isUpdating}
                                    >
                                        {isCreating || isUpdating ? (
                                            <>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                در حال ذخیره...
                                            </>
                                        ) : (
                                            'ذخیره'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                ) : (
                <Paper sx={{ p: 3 }}>
                    {/* Page Title */}
                    <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            یادداشت برای همکاران
                        </Typography>
                    </Box>
                    {/* Header with search, filters, and create button */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                placeholder="جستجو در عنوان و محتوا..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ ml: 1, color: 'text.secondary' }} />,
                                }}
                                sx={{ flexGrow: 1, minWidth: '200px' }}
                            />
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleCreate}
                                sx={{ minWidth: '120px' }}
                            >
                                یادداشت جدید
                            </Button>
                        </Box>
                        
                        {/* Filters */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>اولویت</InputLabel>
                                <Select
                                    value={priorityFilter}
                                    label="اولویت"
                                    onChange={(e) => setPriorityFilter(e.target.value)}
                                >
                                    <MenuItem value="all">همه</MenuItem>
                                    {PRIORITY_OPTIONS.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={myNotesFilter}
                                        onChange={(e) => setMyNotesFilter(e.target.checked)}
                                    />
                                }
                                label="یادداشت‌های من"
                            />
                            
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={pinnedFilter}
                                        onChange={(e) => setPinnedFilter(e.target.checked)}
                                    />
                                }
                                label="ثابت شده"
                            />
                        </Box>
                    </Box>

                    {/* Notes table */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error?.message || 'خطا در بارگذاری یادداشت‌ها'}
                        </Alert>
                    ) : notes.length === 0 ? (
                        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                            {searchTerm || priorityFilter !== 'all' || myNotesFilter || pinnedFilter
                                ? 'یادداشتی یافت نشد.'
                                : 'هنوز یادداشتی ایجاد نکرده‌اید.'}
                        </Alert>
                    ) : (
                        <StandardTable
                            columns={columns}
                            rows={notes}
                            onRowClick={handleEdit}
                            getRowKey={(row) => row.id}
                        />
                    )}

                    {totalCount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                            تعداد کل: {toPersianDigits(totalCount.toString())}
                        </Typography>
                    )}
                </Paper>
                )}

                {/* Delete Confirmation Dialog */}
                <ConfirmationDialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setNoteToDelete(null);
                    }}
                    onConfirm={confirmDelete}
                    title="تأیید حذف"
                    message="آیا مطمئن هستید که می‌خواهید این یادداشت را حذف کنید؟"
                    confirmColor="error"
                    isLoading={isDeleting}
                />

                {/* Snackbar for notifications */}
                <FloatingSnackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
                
                {/* Reply Thread Dialog */}
                <Dialog
                    open={replyDialogOpen}
                    onClose={() => {
                        setReplyDialogOpen(false);
                        setSelectedNoteForReply(null);
                    }}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: { minHeight: '500px', maxHeight: '80vh' },
                    }}
                >
                    <DialogContent>
                        {selectedNoteForReply && (
                            <ReplyThread
                                noteId={selectedNoteForReply.id}
                                onClose={() => {
                                    setReplyDialogOpen(false);
                                    setSelectedNoteForReply(null);
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </RTL>
    );
}

export default ColleagueNotes;

